import * as React from 'react';
import {createViewConfig} from '@sail/engine';
import type {View} from '../view';
import {view} from '../view';
import {css} from '../css';

export type PropertyListProps = View.IntrinsicElement<
  'div',
  {
    /** One or more `PropertyListItem` components */
    children: React.ReactNode;
    /** The orientation of the list */
    orientation?: 'vertical' | 'horizontal';
  }
>;

export const PropertyListConfig = createViewConfig({
  props: {} as PropertyListProps,
  name: 'PropertyList',
  flattens: true,
  defaults: {
    orientation: 'vertical',
  },
});

export const PropertyListItemConfig = createViewConfig({
  props: {} as PropertyListItemProps,
  name: 'PropertyListItem',
  flattens: true,
});

export const PropertyList = PropertyListConfig.createView('div', {
  css: {
    font: 'label.medium',
  },
  variants: {
    orientation: {
      horizontal: {
        css: {
          gapX: 'xxlarge',
          gapY: 'xsmall',
          display: 'grid',
          gridTemplateColumns: 'max-content minmax(0, 1fr)',
          alignItems: 'start',
        },
        uses: [
          PropertyListItemConfig.customize({
            uses: [css({display: 'contents'})],
          }),
        ],
      },
      vertical: {css: {stack: 'y', gap: 'medium'}},
    },
  },
});

export type PropertyListItemProps = View.IntrinsicElement<
  'div',
  {
    /** A label for the item */
    label: React.ReactNode;
    /** The value of the item */
    value?: React.ReactNode;
    subviews?: View.Subviews<{
      label: 'div';
      value: 'div';
    }>;
  }
>;

export const PropertyListItem = PropertyListItemConfig.createView(
  ({label, value, subviews, ...props}) => {
    return (
      <view.div {...props}>
        <view.div inherits={subviews.label}>{label}</view.div>
        {value || typeof value === 'boolean' ? (
          <view.div inherits={subviews.value}>
            {typeof value === 'boolean' ? value.toString() : value}
          </view.div>
        ) : null}
      </view.div>
    );
  },
  {
    css: {
      stack: 'y',
      gap: 'xxsmall',
    },
    subviews: {
      label: {
        css: {
          fontWeight: 'bold',
          overflow: 'hidden',
          wordWrap: 'break-word',
        },
      },
      value: {
        css: {
          marginX: 'space.0',
          marginBottom: 'xxsmall',
          overflow: 'hidden',
          wordWrap: 'break-word'
        },
      },
    },
    uses: [
      PropertyListItemConfig.customize({
        css: {
          stack: 'x',
          gap: 'medium',
        },
        subviews: {
          label: {
            css: {
              color: 'subdued',
              fontWeight: 'normal',
            },
          },
        },
      }),
    ],
  },
);
